/**
 * Created by Oliver Eliardsson on 2024-03-06.
 */

import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {AssetLoader} from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {SpineAssetParser} from "@nolimitcity/slot-game/bin/asset/parsers/SpineAssetParser";
import {Asset, AssetsGroup} from "@nolimitcity/slot-game/bin/asset/Asset";


export class SpinePreviewer extends PIXI.Container implements StageLayer {

    private spineParser:SpineAssetParser;

    private selectedSpine:PIXI.spine.Spine|undefined;
    private previewerWindow:Window|null = null;


    constructor(loader:AssetLoader, gameAssets:any) {
        super();
        this.name = "SpinePreviewer";
        if(NolimitConfig.isDevMode) {
            this.spineParser = (<any>loader)._spineAssetParser;
            this.init();

            // Try to force load spine assets in order to load them to cache
            const resourceGroups = gameAssets.RESOURCES_GROUPS;
            if(!resourceGroups && !resourceGroups.main) {
                console.warn("Invalid gameAssets object passed to spine previewer; could not preload spine resources!");
            } else {
                const spineAssets = resourceGroups.main.find((s:AssetsGroup) => s.name == "spine");
                if(spineAssets) {
                    spineAssets.assets.forEach((asset:Asset) => {
                        const spine = new PIXI.spine.Spine(this.spineParser.getSpineAsset(asset.name));
                        spine.destroy();
                    })
                }
            }
        }
    }

    private getSpineNames():string[] {
        return Object.keys((<any>this.spineParser)._spineDataCache);
    }

    private getHTML():string {
        return `
            <html lang="en">
                <head>
                    <meta charset="UTF-8">
                    <title>Spine Viewer</title>
                </head>
                <style>
                    .caret {
                        cursor: pointer;
                        user-select: none;
                    }
                    .caret::before {
                        content: "\\25B6";
                        color: black;
                        display: inline-block;
                        margin-right: 6px;
                    }
                    .caret-down::before {
                        transform: rotate(90deg);
                    }
                    .nested {
                        display: none;
                    }
                    .active {
                        display: block;
                    }
                </style>
                <body>
                    <b>Left click animation to play on loop</b>
                    <br>
                    <b>Right click animation to play once</b>
                </body>
            </html>
        `;
    }

    private getSpineAnimations(spineName:string):string[] {
        const spine = this.spineParser.getSpineAsset(spineName);
        if(spine) {
            return spine.animations.map(a => a.name);
        } else {
            return [];
        }
    }

    private getSpineSkins(spineName:string):string[] {
        const spine = this.spineParser.getSpineAsset(spineName);
        return spine.skins.map(s => s.name);
    }

    private createAndDisplayAnimation(spine:string, animation:string, skin:string, loop:boolean = true) {
        if(this.selectedSpine) {
            this.removeChild(this.selectedSpine);
        }
        this.selectedSpine = new PIXI.spine.Spine(this.spineParser.getSpineAsset(spine));
        this.selectedSpine.state.setAnimation(0, animation, loop);
        this.selectedSpine.skeleton.setSkinByName(skin);
        this.addChild(this.selectedSpine);
    }

    private createWindow():void {
        const params = `popup=yes,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=300,left=100,top=100`;
        this.previewerWindow = window.open(undefined, 'SpinePreviewer', params);
        if(this.previewerWindow) {
            const document:Document = this.previewerWindow.document;

            this.previewerWindow.document.body.insertAdjacentHTML("afterbegin", this.getHTML());
            let childClicked = false;
            this.getSpineNames().forEach(name => {
                const spineNode = document.createElement("div");
                spineNode.className = "caret";
                spineNode.textContent = name;
                spineNode.addEventListener("click", () => {
                    if(!childClicked) {
                        spineNode.querySelector(".nested")?.classList.toggle("active");
                    } else {
                        childClicked = false;
                    }
                });

                const nestedNode = document.createElement("ul");
                nestedNode.className = "nested";

                // List skins
                let selectedSkin = "default";
                const spineSkins = this.getSpineSkins(name);
                if(spineSkins.length > 1) {
                    const list = document.createElement("select");
                    list.addEventListener("click", (ev) => {
                        childClicked = true;
                    })
                    list.onchange = (ev) => {
                        selectedSkin = spineSkins[list.selectedIndex];
                    };

                    spineSkins.forEach(skin => {
                        const listEntry = new Option(skin, skin);
                        list.appendChild(listEntry);
                    })
                    nestedNode.appendChild(document.createTextNode("skin:  "));
                    nestedNode.appendChild(list);
                    nestedNode.appendChild(document.createElement("br")); // New line after skin select box
                }

                this.getSpineAnimations(name).forEach((animation, index) => {
                    const animationNode = document.createElement("button");
                    animationNode.addEventListener("click", () => {
                        childClicked = true;
                        this.createAndDisplayAnimation(name, animation, selectedSkin);
                    });
                    animationNode.addEventListener("contextmenu", (ev) => {
                        ev.preventDefault();
                        childClicked = true;
                        this.createAndDisplayAnimation(name, animation, selectedSkin, false);
                        return false;
                    });
                    animationNode.textContent = animation;
                    nestedNode.appendChild(animationNode);
                });
                spineNode.appendChild(nestedNode);

                document.body.appendChild(spineNode);
            });

            // Popup windows does not emit a closed event so we have to do this hackjob to detect if window has been closed..
            const timer = setInterval(() => {
                if(this.previewerWindow?.closed) {
                    clearInterval(timer);
                    this.previewerWindow = null;
                    if(this.selectedSpine) {
                        this.removeChild(this.selectedSpine);
                        this.selectedSpine = undefined;
                    }
                }
            }, 1000);
        }
    }

    private init() {
        this.position.set(360, 360);

        document.addEventListener("keydown", (e) => {
            if(e.key == "p" || e.key == "P") {
                if(this.previewerWindow) {
                    this.previewerWindow.focus();
                } else {
                    this.createWindow();
                }
            }
        });
    }
}